import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Pagination,
  Stack
} from '@mui/material';
import { getNewsByCategory, NEWS_CATEGORIES } from '../../services/newsService';
import NewsCard from '../../components/NewsCard';

const ITEMS_PER_PAGE = 10;

function NewsCategory() {
  const { '*': urlPath } = useParams();
  const pathToCategory = {
    'notices': NEWS_CATEGORIES.NOTICE,
    'notifications': NEWS_CATEGORIES.NOTIFICATION,
    'circulars': NEWS_CATEGORIES.CIRCULAR
  };
  const category = pathToCategory[urlPath];

  useEffect(() => {
    if (!category) {
      setError('Invalid category');
      setLoading(false);
      return;
    }
  }, [category]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  const categoryTitle = {
    [NEWS_CATEGORIES.NOTICE]: 'Notices',
    [NEWS_CATEGORIES.NOTIFICATION]: 'Notifications',
    [NEWS_CATEGORIES.CIRCULAR]: 'Circulars'
  }[category];

  useEffect(() => {
    if (category) {
      fetchNews();
    }
  }, [category]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const newsData = await getNewsByCategory(category);
      setNews(newsData);
    } catch (error) {
      setError('Error fetching news: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo(0, 0);
  };

  const paginatedNews = news.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  const pageCount = Math.ceil(news.length / ITEMS_PER_PAGE);

  if (!categoryTitle) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Invalid category</Alert>
      </Container>
    );
  }

  return (
    <Box 
      sx={{ 
        flexGrow: 1,
        bgcolor: '#f8f9fa',
        minHeight: 'calc(100vh - 104px)',
        pt: { xs: 2, sm: 4 },
        pb: { xs: 4, sm: 6 }
      }}
    >
      <Container maxWidth="md">
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            color: '#580100',
            fontWeight: 600,
            mb: { xs: 2, sm: 4 }
          }}
        >
          {categoryTitle}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#580100' }} />
          </Box>
        ) : news.length === 0 ? (
          <Alert severity="info">No {categoryTitle.toLowerCase()} found</Alert>
        ) : (
          <>
            {paginatedNews.map((item) => (
              <NewsCard key={item.id} news={item} />
            ))}
            
            {pageCount > 1 && (
              <Stack alignItems="center" sx={{ mt: 4 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: '#580100',
                    },
                    '& .Mui-selected': {
                      bgcolor: '#580100 !important',
                      color: 'white !important'
                    }
                  }}
                />
              </Stack>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

export default NewsCategory;
